import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../../theme";
import WritingHandwritingPad from "../WritingHandwritingPad";
import { WRITING_CAST, WRITING_L7, profileWritingName } from "../../../../data/writingCurriculum";
import {
  Board,
  CastPicture,
  ChoiceButton,
  ChoiceRow,
  CollectedStrip,
  GameFrame,
  RepeatDots,
  RoundBar,
  RoundCounter,
  ScorePill,
  SentenceFrame,
  StickerTile,
  TargetChip,
  TimerPill,
  useCompleteOnce,
  useCountdown,
  useSpokenItem,
  useWrongFlash,
  withAlpha,
} from "./shared";

/* ── My Profile Name ────────────────────────────────────────────────────── */
export const MyProfileNameGame = ({ activity, profile, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const name = profileWritingName(profile?.name || "");
  const finish = useCompleteOnce(onComplete);
  const [phase, setPhase] = useState("trace");
  return (
    <GameFrame>
      <RoundBar
        left={<TargetChip glyph={name} caption={phase === "trace" ? "Trace your name" : "Write it from memory"} accent={activity.accent} />}
        right={<RepeatDots total={2} done={phase === "trace" ? 0 : 1} accent={activity.accent} />}
      />
      <WritingHandwritingPad
        key={phase}
        target={name}
        accent={activity.accent}
        showsGuide={phase === "trace"}
        mode="word"
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={() => {
          if (phase === "trace") {
            speak?.(`Beautiful. Now write ${name} from memory.`, { interrupt: true });
            setPhase("memory");
          } else finish();
        }}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Meet the Cast: tap a friend, the name appears sound by sound ───────── */
const RevealName = ({ name, revealed, accent, reduceMotion }) => (
  <div aria-label={revealed ? name : "Name hidden"} style={{ display: "flex", gap: 4, justifyContent: "center", minHeight: 34 }}>
    {name.split("").map((ch, i) => (
      <motion.span
        key={`${name}-${i}`}
        initial={false}
        animate={revealed ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0.25, y: 4, scale: 0.9 }}
        transition={{ type: "spring", stiffness: 420, damping: 20, delay: revealed && !reduceMotion ? i * 0.16 : 0 }}
        style={{
          width: 30,
          height: 34,
          borderRadius: 9,
          background: revealed ? "#fff" : withAlpha(accent, 0.15),
          border: `2px solid ${revealed ? withAlpha(accent, 0.5) : "transparent"}`,
          display: "grid",
          placeItems: "center",
          fontWeight: 700,
          fontSize: 20,
          color: accent,
        }}
      >
        {revealed ? ch : ""}
      </motion.span>
    ))}
  </div>
);

export const MeetTheCastGame = ({ activity, speak, sfx, onComplete, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [seen, setSeen] = useState([]);
  const tap = (member) => {
    sfx?.("pop", { volume: 0.5 });
    speak?.(`This is ${member.name}. ${member.name.split("").join(", ")}. ${member.name}.`, { interrupt: true });
    const next = seen.includes(member.name) ? seen : [...seen, member.name];
    setSeen(next);
    if (next.length >= WRITING_CAST.length) window.setTimeout(finish, reduceMotion ? 300 : 1800);
  };
  return (
    <GameFrame center>
      <Board
        title={activity.title}
        symbol={activity.symbol}
        accent={activity.accent}
        right={<RoundCounter done={seen.length} total={WRITING_CAST.length} accent={activity.accent} label="Friends" />}
        caption="Tap each friend"
      >
      <ChoiceRow gap={22}>
        {WRITING_CAST.map((member, i) => (
          <motion.div key={member.name} initial={reduceMotion ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 360, damping: 22, delay: reduceMotion ? 0 : i * 0.08 }} style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
            <CastPicture
              name={member.assetName}
              alt={member.name}
              ariaLabel={`Meet ${member.name}`}
              size="calc(var(--tile, 72px) * 1.75)"
              accent={activity.accent}
              correct={seen.includes(member.name)}
              onClick={() => tap(member)}
              reduceMotion={reduceMotion}
            />
            <RevealName name={member.name} revealed={seen.includes(member.name)} accent={activity.accent} reduceMotion={reduceMotion} />
          </motion.div>
        ))}
      </ChoiceRow>
      </Board>
    </GameFrame>
  );
};

/* ── Trace the Cast / Who Is It? / Name Tags ────────────────────────────── */
const CastWriteLoop = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion, caption, afterTrace = false, showName = true, tags = false }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState(afterTrace ? "trace" : "memory");
  const [done, setDone] = useState([]);
  const member = WRITING_CAST[index];
  const nameVisible = showName || phase === "trace";

  useSpokenItem(speak, `This is ${member.name}.`);

  return (
    <GameFrame>
      <RoundBar
        left={
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <CastPicture name={member.assetName} alt={member.name} size="calc(var(--tile, 72px) * 0.95)" accent={activity.accent} />
            <TargetChip glyph={member.name} caption={phase === "trace" ? "Trace" : caption} accent={activity.accent} hidden={!nameVisible} />
          </div>
        }
        right={<RoundCounter done={index} total={WRITING_CAST.length} accent={activity.accent} />}
      />
      {tags && <CollectedStrip items={done} accent={activity.accent} reduceMotion={reduceMotion} empty="Name tags pop in here" />}
      <WritingHandwritingPad
        key={`${member.name}-${phase}`}
        target={member.name}
        accent={activity.accent}
        showsGuide={phase === "trace"}
        mode="word"
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={() => {
          if (phase === "trace" && afterTrace) {
            setPhase("memory");
            speak?.(`Now write ${member.name} from memory.`, { interrupt: true });
            return;
          }
          setDone((d) => [...d, member.name]);
          speak?.(`${member.name}!`, { interrupt: true });
          if (index + 1 >= WRITING_CAST.length) finish();
          else {
            setIndex((n) => n + 1);
            setPhase(afterTrace ? "trace" : "memory");
          }
        }}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

export const TraceTheCastGame = (props) => <CastWriteLoop {...props} caption="Write" afterTrace />;
export const WhoIsItGame = (props) => <CastWriteLoop {...props} caption="Who is it? Write the name" showName={false} />;
export const NameTagsGame = (props) => <CastWriteLoop {...props} caption="Make the name tag" tags />;

/* ── Name Scramble ──────────────────────────────────────────────────────── */
export const NameScrambleGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [built, setBuilt] = useState("");
  const [used, setUsed] = useState([]);
  const [write, setWrite] = useState(false);
  const { flash, isWrong } = useWrongFlash();
  const member = WRITING_CAST[index];

  const pick = (letter, i) => {
    if (write || used.includes(i)) return;
    const next = built + letter;
    if (!member.name.startsWith(next)) {
      flash(i);
      onWrong?.();
      speak?.(`Not yet. ${member.name} needs ${member.name[built.length]} next.`, { interrupt: true });
      return;
    }
    sfx?.("snap");
    setBuilt(next);
    setUsed((u) => [...u, i]);
    if (next === member.name) {
      sfx?.("correct");
      speak?.(`${member.name}. Now write it.`, { interrupt: true });
      window.setTimeout(() => setWrite(true), reduceMotion ? 150 : 700);
    }
  };

  const counter = <RoundCounter done={index} total={WRITING_CAST.length} accent={activity.accent} />;

  return (
    <GameFrame center={!write}>
      {write && (
        <RoundBar
          left={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CastPicture name={member.assetName} alt={member.name} size="calc(var(--tile, 72px) * 0.95)" accent={activity.accent} />
              <span style={{ fontWeight: 700, fontSize: 17, color: LEARN.color.muted }}>Write {member.name}</span>
            </div>
          }
          right={counter}
        />
      )}
      {!write ? (
        <Board title={activity.title} symbol={activity.symbol} accent={activity.accent} right={counter} bodyStyle={{ gap: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <CastPicture name={member.assetName} alt={member.name} size="calc(var(--tile, 72px) * 1.1)" accent={activity.accent} />
            <span style={{ fontWeight: 700, fontSize: 20, color: LEARN.color.ink }}>Build {member.name}</span>
          </div>
          <ChoiceRow gap={10}>
            {member.name.split("").map((ch, i) => (
              <StickerTile key={`slot-${i}`} as="div" size="calc(var(--tile, 72px) * 0.85)" accent={activity.accent} correct={i < built.length} style={{ borderStyle: i < built.length ? "solid" : "dashed", borderWidth: i < built.length ? 2 : 2.5, borderColor: i < built.length ? undefined : withAlpha(LEARN.color.ink, 0.22), background: i < built.length ? "#fff" : withAlpha(activity.accent, 0.06), boxShadow: i < built.length ? undefined : "none" }}>
                <span style={{ fontSize: "clamp(24px, calc(var(--tile, 72px) * 0.36), 32px)", lineHeight: 1, color: activity.accent }}>{built[i] || ""}</span>
              </StickerTile>
            ))}
          </ChoiceRow>
          <ChoiceRow gap={12}>
            {member.scrambledLetters.map((letter, i) => (
              <ChoiceButton
                key={`${member.name}-${letter}-${i}`}
                label={letter}
                ariaLabel={`Letter ${letter}`}
                accent={activity.accent}
                onClick={() => pick(letter, i)}
                disabled={used.includes(i)}
                dim={used.includes(i)}
                wrong={isWrong(i)}
                reduceMotion={reduceMotion}
              />
            ))}
          </ChoiceRow>
        </Board>
      ) : (
        <WritingHandwritingPad
          key={member.name}
          target={member.name}
          accent={activity.accent}
          showsGuide={false}
          mode="word"
          sfx={sfx}
          reduceMotion={reduceMotion}
          onAccepted={() => {
            if (index + 1 >= WRITING_CAST.length) finish();
            else {
              setIndex((n) => n + 1);
              setBuilt("");
              setUsed([]);
              setWrite(false);
            }
          }}
          onRejected={() => onWrong?.()}
        />
      )}
    </GameFrame>
  );
};

/* ── Big Letter First ───────────────────────────────────────────────────── */
export const BigLetterFirstGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState("choose");
  const [picked, setPicked] = useState(null);
  const { flash, isWrong } = useWrongFlash();
  const member = WRITING_CAST[index];

  const choose = (letter) => {
    if (picked) return;
    if (letter !== member.name[0]) {
      flash(letter);
      onWrong?.();
      speak?.(`${member.name} starts with ${member.name[0]}. Try again.`, { interrupt: true });
      return;
    }
    sfx?.("correct");
    setPicked(letter);
    speak?.(`Capital ${letter} starts ${member.name}. Now write ${member.name}.`, { interrupt: true });
    window.setTimeout(() => setPhase("write"), reduceMotion ? 150 : 800);
  };

  const done = () => {
    if (index + 1 >= WRITING_CAST.length) finish();
    else {
      setIndex((n) => n + 1);
      setPhase("choose");
      setPicked(null);
    }
  };

  const counter = <RoundCounter done={index} total={WRITING_CAST.length} accent={activity.accent} />;

  return (
    <GameFrame center={phase === "choose"}>
      {phase !== "choose" && (
        <RoundBar
          left={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CastPicture name={member.assetName} alt={member.name} size="calc(var(--tile, 72px) * 0.95)" accent={activity.accent} />
              <TargetChip glyph={member.name} caption="Write" accent={activity.accent} />
            </div>
          }
          right={counter}
        />
      )}
      {phase === "choose" ? (
        <Board title={activity.title} symbol={activity.symbol} accent={activity.accent} right={counter} bodyStyle={{ gap: 22 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <CastPicture name={member.assetName} alt={member.name} size="calc(var(--tile, 72px) * 1.1)" accent={activity.accent} />
            <span style={{ fontWeight: 700, fontSize: 20, color: LEARN.color.ink }}>Which capital starts {member.name}?</span>
          </div>
          <ChoiceRow gap={18}>
            {member.firstLetterChoices.map((letter, i) => (
              <motion.div key={`${member.name}-${letter}`} initial={reduceMotion ? false : { opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 360, damping: 22, delay: reduceMotion ? 0 : i * 0.07 }}>
                <ChoiceButton
                  label={letter}
                  ariaLabel={`Capital ${letter}`}
                  accent={activity.accent}
                  size="calc(var(--tile, 72px) * 1.3)"
                  onClick={() => choose(letter)}
                  wrong={isWrong(letter)}
                  correct={picked === letter}
                  dim={!!picked && picked !== letter}
                  reduceMotion={reduceMotion}
                />
              </motion.div>
            ))}
          </ChoiceRow>
        </Board>
      ) : (
        <WritingHandwritingPad key={member.name} target={member.name} accent={activity.accent} showsGuide={false} mode="word" sfx={sfx} reduceMotion={reduceMotion} onAccepted={done} onRejected={() => onWrong?.()} />
      )}
    </GameFrame>
  );
};

/* ── Character in a Sentence ────────────────────────────────────────────── */
export const CharacterInSentenceGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [index, setIndex] = useState(0);
  const [filled, setFilled] = useState("");
  const round = WRITING_L7.sentences[index];
  const member = WRITING_CAST.find((m) => m.name === round.memberName);

  useSpokenItem(speak, `${round.before}${round.memberName}${round.after}`);

  return (
    <GameFrame>
      <RoundBar
        left={
          <SentenceFrame before={round.before} after={round.after} filled={filled} accent={activity.accent} blankWidth="3.4em">
            <CastPicture name={member.assetName} alt={member.name} size="calc(var(--tile, 72px) * 0.95)" accent={activity.accent} />
          </SentenceFrame>
        }
        right={<RoundCounter done={index} total={WRITING_L7.sentences.length} accent={activity.accent} />}
      />
      <WritingHandwritingPad
        key={member.name}
        target={member.name}
        accent={activity.accent}
        showsGuide={false}
        mode="word"
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={() => {
          setFilled(member.name);
          speak?.(`${round.before}${member.name}${round.after}`, { interrupt: true });
          window.setTimeout(() => {
            setFilled("");
            if (index + 1 >= WRITING_L7.sentences.length) finish();
            else setIndex((n) => n + 1);
          }, reduceMotion ? 200 : 900);
        }}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Name Sprint ────────────────────────────────────────────────────────── */
export const NameSprintGame = ({ activity, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const finish = useCompleteOnce(onComplete);
  const [choice, setChoice] = useState(null);
  const [written, setWritten] = useState([]);
  const writtenRef = useRef(written);
  writtenRef.current = written;
  const seconds = useCountdown(activity.seconds || 60, { running: !!choice, onEnd: finish });

  if (!choice) {
    return (
      <GameFrame center>
        <Board title={activity.title} symbol={activity.symbol} accent={activity.accent} caption="Choose one friend to write">
          <ChoiceRow gap={22}>
            {WRITING_CAST.map((member, i) => (
              <motion.div key={member.name} initial={reduceMotion ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 360, damping: 22, delay: reduceMotion ? 0 : i * 0.08 }} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                <CastPicture
                  name={member.assetName}
                  alt={member.name}
                  ariaLabel={`Write ${member.name}`}
                  size="calc(var(--tile, 72px) * 1.6)"
                  accent={activity.accent}
                  onClick={() => { sfx?.("pop", { volume: 0.5 }); setChoice(member); speak?.(`${member.name}. Write it as many times as you like.`, { interrupt: true }); }}
                  reduceMotion={reduceMotion}
                />
                <span style={{ fontWeight: 700, fontSize: 20, color: LEARN.color.ink }}>{member.name}</span>
              </motion.div>
            ))}
          </ChoiceRow>
        </Board>
      </GameFrame>
    );
  }

  return (
    <GameFrame>
      <RoundBar
        left={
          <>
            <TimerPill seconds={seconds} accent={activity.accent} />
            <TargetChip glyph={choice.name} accent={activity.accent} size="calc(var(--tile, 72px) * 0.8)" />
          </>
        }
        right={<ScorePill score={written.length} unit={written.length === 1 ? "name" : "names"} accent={activity.accent} />}
      />
      <CollectedStrip items={written} accent={activity.accent} reduceMotion={reduceMotion} empty="Each finished name pops in here" />
      <WritingHandwritingPad
        key={written.length}
        target={choice.name}
        accent={activity.accent}
        showsGuide={false}
        mode="word"
        sfx={sfx}
        reduceMotion={reduceMotion}
        acceptDelay={reduceMotion ? 100 : 480}
        onAccepted={() => { speak?.(choice.name, { interrupt: true }); setWritten((w) => [...w, choice.name]); }}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};

/* ── Sign My Book ───────────────────────────────────────────────────────── */
export const SignMyBookGame = ({ activity, profile, speak, sfx, onComplete, onWrong, reduceMotion }) => {
  const name = profileWritingName(profile?.name || "");
  const finish = useCompleteOnce(onComplete);
  const [signed, setSigned] = useState(false);
  return (
    <GameFrame>
      <div
        style={{
          flexShrink: 0,
          borderRadius: LEARN.radius.lg,
          background: "linear-gradient(180deg, #FFF8E4 0%, #FFEFC7 100%)",
          border: `3px solid ${withAlpha(LEARN.color.sun, 0.7)}`,
          boxShadow: LEARN.shadow.card,
          padding: "12px 18px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 14,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, fontSize: 24, color: LEARN.color.ink }}>My Book</div>
          <div style={{ fontSize: 14, color: LEARN.color.muted, fontWeight: 600 }}>by</div>
        </div>
        <motion.div
          animate={signed ? { scale: [1, 1.08, 1] } : { scale: 1 }}
          style={{
            minWidth: "40%",
            borderBottom: `3px solid ${signed ? activity.accent : withAlpha(activity.accent, 0.4)}`,
            textAlign: "center",
            fontWeight: 700,
            fontSize: 26,
            color: activity.accent,
            padding: "0 8px 4px",
            fontStyle: signed ? "italic" : "normal",
            minHeight: 36,
          }}
        >
          {signed ? name : ""}
        </motion.div>
      </div>
      <WritingHandwritingPad
        target={name}
        accent={activity.accent}
        showsGuide={false}
        mode="word"
        sfx={sfx}
        reduceMotion={reduceMotion}
        onAccepted={() => {
          setSigned(true);
          speak?.(`${name} signed My Book.`, { interrupt: true });
          window.setTimeout(finish, reduceMotion ? 200 : 900);
        }}
        onRejected={() => onWrong?.()}
      />
    </GameFrame>
  );
};
